/**
 * Godot surface state that three holds per object, applied per draw group: a billboard's
 * cameras, the node's pose a program reads, the layers a probe's mask meets, shadow-pass membership,
 * the shadow's alpha cut and SHADOWS_ONLY. three calls each before-hook and its
 * after-hook around one group's draw, with that group's material (r187 `WebGLRenderer.js:2171-2196`,
 * `WebGLShadowMap.js:548-566`), so one mesh can mix surfaces the way Godot does.
 */
import * as THREE from 'three';
import { BillboardMode } from '../godot/rendering/materials/billboardMode';
import { billboardOf, castsShadowOf } from '../resources/materials/standardmaterial3d/materialBag';
import { aimBillboards } from './surfaceBillboard';
import { restoreInstanceLayers, showInstanceLayers } from './instanceLayers/instanceLayers';
import { alignNodePose } from './materials/nodePose';
import { opaquePrepassOf } from './materials/opaquePrepass';
import { surfaceDepthMaterial, syncSurfaceDepth } from './materials/surfaceDepthMaterial';
import { addsDepthInjections } from './materials/depthInjections';
import { SHADOW_PASS_OPAQUE_THRESHOLD } from '../godot/rendering/opaquePrepass';
/** An instance with no visibility range, posed by its object alone. */
export const UNRANGED_INSTANCE = Object.freeze({
    isVisible: true,
    nodeMatrixWorld: () => false,
});
/** The material three draws for `group` of `object`, from inside a shadow hook. */
function drawnMaterial(object, group) {
    const material = object?.material;
    if (!Array.isArray(material))
        return material;
    return material[group?.materialIndex ?? 0];
}
/**
 * What the open draw changed, for its after-hook to put back. Written only by a
 * before-hook and cleared by the after-hook three calls next: three draws one group at
 * a time, so no two draws are ever open together.
 */
let skipped = null;
let skippedCount = Infinity;
/** Scratch for the node's matrix, overwritten by every draw. */
const nodeMatrix = new THREE.Matrix4();
/**
 * Make this draw draw nothing: an empty `drawRange` gives the GPU zero elements
 * (r187 `WebGLRenderer.js:1227-1252`). On the geometry, which a clone or an overlay shares,
 * so the after-hook puts the count back before any other draw.
 */
function skip(geometry) {
    skipped = geometry;
    skippedCount = geometry.drawRange.count;
    geometry.drawRange.count = 0;
}
/**
 * Aims this draw's billboard, which its program builds, at the cameras of this render: Godot's
 * shadow pass keeps the main camera as `MAIN_CAM_INV_VIEW_MATRIX` (`material.cpp:1263-1264`).
 */
function aimBillboard(material, mainCamera, passCamera) {
    if (billboardOf(material).mode === BillboardMode.BILLBOARD_DISABLED)
        return;
    aimBillboards(mainCamera, passCamera);
}
/** Gives this draw of `material` the node's pose, which Godot's MODEL_MATRIX holds (`nodePose.ts`). */
function alignPose(object, material, instance) {
    if (!instance.nodeMatrixWorld(nodeMatrix))
        nodeMatrix.copy(object.matrixWorld);
    alignNodePose(material, nodeMatrix, object.matrixWorld);
}
/**
 * The alpha below which a shadow draw of `material` writes nothing, or 0 for none: its scissor, and
 * the shadow pass's opaque-prepass threshold.
 */
function shadowAlphaCut(material) {
    const prepassCut = opaquePrepassOf(material).cutsDepth ? SHADOW_PASS_OPAQUE_THRESHOLD : 0;
    return Math.max(material.alphaTest, prepassCut);
}
/**
 * Draws a surface whose shadow needs its own depth material, and makes three's draw draw nothing.
 * three's would cut the texture alpha alone, and at the scissor only, and add none of the depth
 * injections the draw records (`depthInjections.ts`).
 */
function castOwnShadow(renderer, object, shadowCamera, geometry, depthMaterial, material, group, instance) {
    const own = surfaceDepthMaterial(material, depthMaterial);
    syncSurfaceDepth(own, material, shadowAlphaCut(material));
    own.side = depthMaterial.side;
    // three hands a distance material its light inside `getDepthMaterial` (r187 `WebGLShadowMap.js:509-514`).
    if (depthMaterial.isMeshDistanceMaterial) {
        lightState(renderer, own).light = lightState(renderer, depthMaterial).light;
    }
    alignPose(object, own, instance);
    // The scene three's own shadow draw passes: none (r187 `WebGLShadowMap.js:550`), typed as one.
    renderer.renderBufferDirect(shadowCamera, null, geometry, own, object, group);
    skip(geometry);
}
/** The renderer's state for a distance material, which three types as unknown. */
function lightState(renderer, material) {
    return renderer.properties.get(material);
}
/** Whether a shadow draw of `material` cuts its alpha anywhere. */
function cutsShadow(material) {
    return material.alphaHash || shadowAlphaCut(material) > 0;
}
/**
 * Whether three's depth material cannot draw `material`'s shadow: it cuts alpha, or its draw adds
 * to the depth program, as a points size, a billboard or a particle colour does.
 */
function needsOwnDepth(material) {
    return cutsShadow(material) || addsDepthInjections(material.userData);
}
function closeDraw() {
    restoreInstanceLayers();
    if (skipped) {
        skipped.drawRange.count = skippedCount;
        skipped = null;
    }
}
/** A drawing colour pass: the surface billboards if its material says so. */
export const poseColourDraw = (_object, _geometry, material, camera) => {
    aimBillboard(material, camera, camera);
};
/** SHADOWS_ONLY's colour pass: the surface draws nothing, and still casts. */
export const skipColourDraw = (_object, geometry) => {
    skip(geometry);
};
/**
 * The hooks for one `cast_shadow` rule of one instance. A culled instance draws no colour and
 * casts into no directional shadow (`renderer_scene_cull.cpp:2852,3140`). It still casts into an
 * omni or spot shadow, whose cull reads no range (`:2415`).
 */
export function surfaceDrawHooks(rule, instance) {
    return {
        onBeforeRender(renderer, _scene, camera, geometry, material) {
            if (!instance.isVisible) {
                skip(geometry);
                return;
            }
            rule.colourDraw(this, geometry, material, camera);
            alignPose(this, material, instance);
            showInstanceLayers(renderer, this);
        },
        onAfterRender: closeDraw,
        onBeforeShadow(renderer, object, camera, shadowCamera, geometry, depthMaterial, group) {
            const material = drawnMaterial(object, group);
            if (!material)
                return;
            rule.shadowSide(depthMaterial, material);
            // three gives only a DirectionalLight's shadow an orthographic camera.
            if (!instance.isVisible && shadowCamera.isOrthographicCamera) {
                skip(geometry);
                return;
            }
            // Godot's render list leaves such a surface out (`render_forward_clustered.cpp:4078-4088`).
            if (!castsShadowOf(material)) {
                skip(geometry);
                return;
            }
            // three passes the object itself here, typed as the scene.
            const drawn = object;
            aimBillboard(material, camera, shadowCamera);
            if (!needsOwnDepth(material))
                return;
            const shadowDepth = depthMaterial;
            // three passes the geometry group, or null for one material, typed as a `Group`.
            const drawnGroup = group;
            castOwnShadow(renderer, drawn, shadowCamera, geometry, shadowDepth, material, drawnGroup, instance);
        },
        onAfterShadow: closeDraw,
    };
}
//# sourceMappingURL=surfaceDrawHooks.js.map